import React, { useState } from 'react';

const AmbassadorTable = ({ ambassadors, pagination, loading, onPageChange, onRecordPayment, onAmbassadorClick }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!ambassadors || ambassadors.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        No ambassadors found.
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const toggleDropdown = (ambassadorId) => {
    setOpenDropdown(openDropdown === ambassadorId ? null : ambassadorId);
  };

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-hover mb-0">
            <thead className="table-light" style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th scope="col" className="px-4 py-3 fw-semibold text-uppercase small text-muted" style={{ width: '8%' }}>Rank</th>
                <th scope="col" className="px-4 py-3 fw-semibold text-uppercase small text-muted" style={{ width: '25%' }}>Name</th>
                <th scope="col" className="px-4 py-3 fw-semibold text-uppercase small text-muted text-center" style={{ width: '18%' }}>Users Onboarded</th>
                <th scope="col" className="px-4 py-3 fw-semibold text-uppercase small text-muted" style={{ width: '18%' }}>Paid vs Pending</th>
                <th scope="col" className="px-4 py-3 fw-semibold text-uppercase small text-muted text-end" style={{ width: '13%' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="border-top">
              {ambassadors.map((ambassador) => (
                <tr 
                  key={ambassador.id}
                  className="border-bottom"
                  style={{ 
                    transition: 'background-color 0.15s ease-in-out',
                    cursor: onAmbassadorClick ? 'pointer' : 'default'
                  }}
                  onClick={(e) => {
                    // Don't trigger if clicking on dropdown or button
                    if (onAmbassadorClick && !e.target.closest('.dropdown-container') && !e.target.closest('button')) {
                      onAmbassadorClick(ambassador);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (onAmbassadorClick) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                  }}
                >
                  <td className="px-4 py-4">
                    <span className="fw-medium text-dark">#{ambassador.rank}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          backgroundColor: '#4C1D95',
                          fontSize: '0.75rem'
                        }}
                      >
                        {getInitials(ambassador.name)}
                      </div>
                      <div>
                        <div className="fw-medium text-dark">{ambassador.name}</div>
                        {ambassador.universityName && (
                          <div className="text-muted small" style={{ fontSize: '0.75rem' }}>{ambassador.universityName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="fw-bold text-dark" style={{ fontSize: '1rem' }}>{ambassador.usersOnboarded}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-dark">{formatCurrency(ambassador.amountPaid)}</span>
                      <span 
                        className="small fw-medium mt-1"
                        style={{ 
                          color: ambassador.amountPending > 0 ? '#EA580C' : '#64748b',
                          fontSize: '0.75rem'
                        }}
                      >
                        Pending: {formatCurrency(ambassador.amountPending)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-end">
                    <div className="dropdown-container position-relative d-inline-block">
                      <button
                        className="btn btn-sm p-1 border-0 text-muted"
                        type="button"
                        onClick={() => toggleDropdown(ambassador.id)}
                        style={{
                          borderRadius: '50%',
                          transition: 'all 0.15s ease-in-out'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#4C1D95';
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#64748b';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-three-dots-vertical" style={{ fontSize: '1.25rem' }}></i>
                      </button>
                      {openDropdown === ambassador.id && (
                        <div 
                          className="position-absolute end-0 mt-2 rounded-lg shadow-lg border"
                          style={{
                            backgroundColor: 'white',
                            minWidth: '180px',
                            zIndex: 1000,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          <div className="py-1">
                            <button
                              className="dropdown-item w-100 text-start px-4 py-2 border-0 bg-transparent text-dark small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onRecordPayment) {
                                  onRecordPayment(ambassador);
                                  setOpenDropdown(null);
                                } else {
                                  console.error('onRecordPayment handler is not defined');
                                }
                              }}
                              disabled={!onRecordPayment}
                              style={{
                                transition: 'background-color 0.15s ease-in-out',
                                cursor: onRecordPayment ? 'pointer' : 'not-allowed',
                                opacity: onRecordPayment ? 1 : 0.6
                              }}
                              onMouseEnter={(e) => {
                                if (onRecordPayment) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <i className="bi bi-cash-coin me-2"></i>Record Payment
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between border-top px-4 py-3">
          <div className="d-none d-sm-flex align-items-center">
            <p className="text-muted small mb-0">
              Showing <span className="fw-medium text-dark">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
              <span className="fw-medium text-dark">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span> of{' '}
              <span className="fw-medium text-dark">{pagination.total}</span> results
            </p>
          </div>
          <nav aria-label="Pagination">
            <ul className="pagination mb-0" style={{ gap: '0' }}>
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link border rounded-start"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{ 
                    borderColor: '#e2e8f0',
                    color: pagination.page === 1 ? '#94a3b8' : '#64748b'
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                return (
                  <li key={pageNum} className="page-item">
                    <button
                      className={`page-link border-0 ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => onPageChange(pageNum)}
                      style={{
                        backgroundColor: pagination.page === pageNum ? '#4C1D95' : 'transparent',
                        color: pagination.page === pageNum ? 'white' : '#1e293b',
                        fontWeight: pagination.page === pageNum ? '600' : '600',
                        minWidth: '40px'
                      }}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link border rounded-end"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  style={{ 
                    borderColor: '#e2e8f0',
                    color: pagination.page === pagination.totalPages ? '#94a3b8' : '#64748b'
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AmbassadorTable;

